import SplitControl from '@/components/business/SplitControl'
import { OwnerSquad, ScreenName } from '@/analytics/generated/segment'
import type {
  MenuItemProps,
  MenuItemTitleExperiment,
} from '@/resources/types/menu/types'
import { HexaV2NavItem } from '@/components/hexa-v2'
import { HexaIcons } from '@/resources/enums'
import { useSeller } from '@/contexts/seller'
import useSplitExperiment from '@/hooks/useSplitExperiment'
import useManualExperimentExposure from '@/hooks/useManualExperimentExposure'

import {
  getManualExperimentExposureDeduplicationKey,
  getMenuItemExperimentTitle,
} from './utils'
import * as S from './styles'

const NavItem = ({
  menuItem,
  onClick,
  isItemActive,
  isMenuNavigationPending = false,
}: MenuItemProps) => {
  const navItem = (
    <HexaV2NavItem
      active={isItemActive}
      key={menuItem.id}
      // Nav item only renders the icon font. The star keeps the slot width under the custom SVG.
      icon={menuItem.customIconSrc ? HexaIcons.STAR_FILLED : menuItem.icon}
      label={menuItem.text}
      onHxChangeActiveNavItem={() => {
        if (isMenuNavigationPending) {
          return
        }

        onClick?.(menuItem)
      }}
    />
  )

  if (!menuItem.customIconSrc) {
    return navItem
  }

  return (
    <S.CustomIconNavItem>
      {navItem}
      <img src={menuItem.customIconSrc} alt="" />
    </S.CustomIconNavItem>
  )
}

type MenuTitleExperimentNavItemProps = MenuItemProps & {
  titleExperiment: MenuItemTitleExperiment
}

const MenuTitleExperimentNavItem = ({
  menuItem,
  titleExperiment,
  onClick,
  isItemActive,
  isMenuNavigationPending,
}: MenuTitleExperimentNavItemProps) => {
  const { sellerData } = useSeller()
  const splitExperiment = useSplitExperiment({
    splitName: titleExperiment.splitName,
  })

  const ownerSquad =
    titleExperiment.ownerSquad ?? OwnerSquad.DtcZeSellerManagement
  const menuLabel = getMenuItemExperimentTitle(
    menuItem.text,
    titleExperiment.variantTitle,
    splitExperiment.variantName
  )
  const menuItemWithExperimentTitle = {
    ...menuItem,
    text: menuLabel,
  }
  const exposureDeduplicationKey = getManualExperimentExposureDeduplicationKey({
    experimentName: titleExperiment.splitName,
    pocId: sellerData?.seller?.id,
    variantName: splitExperiment.variantName,
  })

  useManualExperimentExposure({
    deduplicationKey: exposureDeduplicationKey,
    experimentName: titleExperiment.splitName,
    isReady: splitExperiment.isReady && Boolean(exposureDeduplicationKey),
    owner: ownerSquad,
    ownerSquad,
    screenName: ScreenName.Home,
    variantId: splitExperiment.variantId,
    variantName: splitExperiment.variantName,
  })

  return (
    <NavItem
      menuItem={menuItemWithExperimentTitle}
      onClick={onClick}
      isItemActive={isItemActive}
      isMenuNavigationPending={isMenuNavigationPending}
    />
  )
}

const SplitControlledMenuItem = ({
  menuItem,
  onClick,
  isItemActive,
  isMenuNavigationPending,
}: MenuItemProps) => {
  const titleExperiment = menuItem.titleExperiment

  if (titleExperiment) {
    return (
      <MenuTitleExperimentNavItem
        menuItem={menuItem}
        titleExperiment={titleExperiment}
        onClick={onClick}
        isItemActive={isItemActive}
        isMenuNavigationPending={isMenuNavigationPending}
      />
    )
  }

  return (
    <>
      {menuItem.featureFlag && menuItem.splitName ? (
        <SplitControl splitName={menuItem.splitName} treatmentOff={null}>
          <NavItem
            menuItem={menuItem}
            onClick={onClick}
            isItemActive={isItemActive}
            isMenuNavigationPending={isMenuNavigationPending}
          />
        </SplitControl>
      ) : (
        <NavItem
          menuItem={menuItem}
          onClick={onClick}
          isItemActive={isItemActive}
          isMenuNavigationPending={isMenuNavigationPending}
        />
      )}
    </>
  )
}

export default SplitControlledMenuItem
